import { useTranslation } from 'react-i18next';
import { ArrowUpRight } from 'lucide-react';
import { UserActivityCounts } from 'src/domain/analytics';
import { AnalyticsCard, AnalyticsChartFrame } from './AnalyticsCard';
import { MetricCards } from './MetricCards';
import { COVERAGE_STATUSES as statuses } from './coverageStatuses';
import { useScopedAnalyticsText } from './analyticsScope';

export const ActiveUsersCard = ({
    usage,
    role,
}: {
    usage: UserActivityCounts;
    role: 'employees' | 'mainContacts';
}) => {
    const { t, i18n } = useTranslation();
    const counts = usage[role];
    return (
        <AnalyticsCard
            title={t(`admin.analytics.active_${role}`)}
            description={t('admin.analytics.activeWindow')}
            className="analytics-active-users"
            info={<ActivityTrackingInfo usage={usage} />}
        >
            <MetricCards
                metrics={(['day', 'week', 'month'] as const).map(window => ({
                    label: t(`admin.analytics.active_${role}_${window}`),
                    value:
                        usage.trackingSince && counts
                            ? counts[window].toLocaleString(i18n.language)
                            : '—',
                }))}
            />
        </AnalyticsCard>
    );
};

/** When tracking started, and that mobile activity needs the current app version. */
export const ActivityTrackingInfo = ({
    usage,
}: {
    usage: UserActivityCounts;
}) => {
    const { t, i18n } = useTranslation();
    return (
        <>
            <p>
                {usage.trackingSince
                    ? t('admin.analytics.trackingSince', {
                          date: new Date(
                              usage.trackingSince,
                          ).toLocaleDateString(i18n.language),
                      })
                    : t('admin.analytics.trackingNotStarted')}
            </p>
            <p>{t('admin.analytics.activityAppNote')}</p>
        </>
    );
};

export const ActiveCoverageCard = ({
    usage,
    residents,
}: {
    usage: UserActivityCounts;
    residents: number;
}) => {
    const { t, i18n } = useTranslation();
    const distribution = usage.activeMainContactCoverage;
    if (!distribution) return null;
    const number = (value: number) => value.toLocaleString(i18n.language);
    const value = (count: number) =>
        usage.trackingSince ? number(count) : '—';
    const covered = distribution
        .slice(1)
        .reduce((sum, count) => sum + count, 0);
    return (
        <AnalyticsCard
            title={t('admin.analytics.activeCoverage')}
            description={t('admin.analytics.activeCoverageNote')}
            info={<ActivityTrackingInfo usage={usage} />}
        >
            <MetricCards
                metrics={[
                    {
                        label: t('admin.analytics.coveredResidents'),
                        value:
                            usage.trackingSince && residents
                                ? `${number(
                                      Math.round((covered / residents) * 1000) /
                                          10,
                                  )}%`
                                : '—',
                        detail: usage.trackingSince
                            ? t('admin.analytics.coveredCount', {
                                  count: covered,
                                  total: residents,
                              })
                            : t('admin.analytics.trackingNotStarted'),
                    },
                ]}
            />
            <dl className="analytics-detail-list">
                {[3, 2, 1, 0].map(index => (
                    <div key={index}>
                        <dt>
                            {t('admin.analytics.activeMainContactLinks', {
                                count: index,
                                label: index === 3 ? '3+' : index,
                            })}
                        </dt>
                        <dd>{value(distribution[index])}</dd>
                    </div>
                ))}
            </dl>
        </AnalyticsCard>
    );
};

export const CurrentCoverageChart = ({
    usage,
    total,
    embedded,
    population = 'residents',
    onNeverLoggedIn,
}: {
    usage: UserActivityCounts;
    total: number;
    embedded?: boolean;
    population?: 'residents' | 'employees';
    onNeverLoggedIn?: () => void;
}) => {
    const { t, i18n } = useTranslation();
    const text = useScopedAnalyticsText();
    const employee = population === 'employees';
    const coverage = employee
        ? usage.employeeCoverage && {
              ...usage.employeeCoverage,
              noLinkedContact: 0,
          }
        : usage.mainContactCoverage;
    if (!coverage) return null;
    const number = (count: number) => count.toLocaleString(i18n.language);
    const observed = Boolean(usage.trackingSince);
    const visibleStatuses = statuses.filter(
        status => !employee || status.key !== 'noLinkedContact',
    );
    const ceiling =
        Math.max(
            1,
            Math.ceil(
                Math.max(...statuses.map(status => coverage[status.key])) / 4,
            ),
        ) * 4;
    const left = 36;
    const top = 22;
    const height = 150;
    const width = 400;
    const percent = total
        ? number(Math.round((coverage.active / total) * 1000) / 10)
        : '0';
    return (
        <AnalyticsChartFrame
            embedded={embedded}
            info={text(
                employee
                    ? 'admin.analytics.employeeCoverageNote'
                    : 'admin.analytics.mainContactCoverageNote',
            )}
            title={t('admin.analytics.mainContactCoverageTitle')}
            description={t('admin.analytics.mainContactCoverageScope')}
        >
            {observed ? (
                <>
                    <p className="analytics-note">
                        {t(
                            employee
                                ? 'admin.analytics.employeeChartSummary'
                                : 'admin.analytics.mainContactChartSummary',
                            {
                                active: number(coverage.active),
                                total: number(total),
                                percent,
                            },
                        )}
                    </p>
                    <div className="analytics-coverage-chart">
                        <svg
                            viewBox="0 0 450 205"
                            className="analytics-chart"
                            aria-hidden="true"
                        >
                            {Array.from({ length: 5 }, (_, index) => {
                                const y = top + height - (index * height) / 4;
                                return (
                                    <g key={index}>
                                        <line
                                            x1={left}
                                            x2={left + width}
                                            y1={y}
                                            y2={y}
                                            className="analytics-chart-grid"
                                        />
                                        <text
                                            x={left - 7}
                                            y={y + 4}
                                            textAnchor="end"
                                        >
                                            {number((index * ceiling) / 4)}
                                        </text>
                                    </g>
                                );
                            })}
                            {visibleStatuses.map((status, index) => {
                                const count = coverage[status.key];
                                const barHeight = (count / ceiling) * height;
                                const x =
                                    left +
                                    ((index + 0.5) * width) /
                                        visibleStatuses.length;
                                return (
                                    <g key={status.key}>
                                        <rect
                                            x={x - 23}
                                            y={top + height - barHeight}
                                            width={46}
                                            height={barHeight}
                                            rx={4}
                                            fill={status.color}
                                        />
                                        <text
                                            x={x}
                                            y={top + height - barHeight - 7}
                                            textAnchor="middle"
                                            className="analytics-chart-count"
                                        >
                                            {number(count)}
                                        </text>
                                    </g>
                                );
                            })}
                        </svg>
                        <div
                            className="analytics-coverage-labels"
                            style={{
                                gridTemplateColumns: `repeat(${visibleStatuses.length}, minmax(0, 1fr))`,
                            }}
                        >
                            {visibleStatuses.map(status => {
                                const label = (
                                    <>
                                        <i
                                            style={{
                                                backgroundColor: status.color,
                                            }}
                                        />
                                        <strong>
                                            {number(coverage[status.key])}
                                        </strong>{' '}
                                        {t(
                                            `admin.analytics.mainContactChart_${status.key}`,
                                        )}
                                    </>
                                );
                                return status.key === 'neverLoggedIn' &&
                                    onNeverLoggedIn &&
                                    coverage.neverLoggedIn > 0 ? (
                                    <button
                                        type="button"
                                        key={status.key}
                                        className="analytics-drilldown-button analytics-drilldown-accent"
                                        onClick={onNeverLoggedIn}
                                        aria-label={t(
                                            employee
                                                ? 'admin.analytics.viewNeverLoggedInEmployees'
                                                : 'admin.analytics.viewNeverLoggedInMainContacts',
                                        )}
                                    >
                                        {label}
                                        <ArrowUpRight
                                            size={13}
                                            strokeWidth={2.25}
                                            aria-hidden="true"
                                        />
                                    </button>
                                ) : (
                                    <span key={status.key}>{label}</span>
                                );
                            })}
                        </div>
                    </div>
                </>
            ) : (
                <p className="analytics-note">
                    {t('admin.analytics.trackingNotStarted')}
                </p>
            )}
            <dl className="sr-only">
                {visibleStatuses.map(status => (
                    <div key={status.key}>
                        <dt>
                            {t(
                                `admin.analytics.${
                                    employee
                                        ? `mainContactChart_${status.key}`
                                        : status.key === 'active'
                                        ? 'withActiveMainContact'
                                        : `mainContact_${status.key}`
                                }`,
                            )}
                        </dt>
                        <dd>{observed ? number(coverage[status.key]) : '—'}</dd>
                    </div>
                ))}
                {!employee && (
                    <div>
                        <dt>{t('admin.analytics.withoutActiveMainContact')}</dt>
                        <dd>
                            {observed ? number(total - coverage.active) : '—'}
                        </dd>
                    </div>
                )}
            </dl>
        </AnalyticsChartFrame>
    );
};
